import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {createStructuredSelector} from 'reselect';

import * as localizationKeys from '../../../consts/localization/localizationKeys';
import ModalTypes from '../../../enums/app/modalTypes';
import useDidUpdate from '../../../hooks/useDidUpdate';
import analyticsService from '../../../services/analyticsService';
import appErrorService from '../../../services/app/appErrorService';
import iotDeviceConnectionStatusService from '../../../services/iotDevice/iotDeviceConnectionStatusService';
import {getLocalizedStrings} from '../../../services/localization/localizationService';
import modalService from '../../../services/modalService';
import productService from '../../../services/product/productService';
import {setNotificationSuccess} from '../../../state/ducks/global';
import {makeSelectIotDeviceMergedWithConsumerProduct} from '../../../state/selectors/consumer';
import {selectIsReconnectGuideEnabled} from '../../../state/selectors/global';
import {selectDeviceConnectionStatus} from '../../../state/selectors/iotDevice';
import {
    makeSelectIsYapDeviceInfoReceived,
    makeSelectIsYapHolderInfoReceived,
} from '../../../state/selectors/yapEncrypted';
import {dispatch} from '../../../state/store';

const mapStateToProps = createStructuredSelector({
    iotProduct: makeSelectIotDeviceMergedWithConsumerProduct(),
    connectionStatus: selectDeviceConnectionStatus,
    isReconnectGuideEnabled: selectIsReconnectGuideEnabled,
    isYapDeviceInfoReceived: makeSelectIsYapDeviceInfoReceived(),
    isYapHolderInfoReceived: makeSelectIsYapHolderInfoReceived(),
});

const DeviceConnectionDetector = (props) => {
    const {iotProduct, connectionStatus, isReconnectGuideEnabled, isYapDeviceInfoReceived, isYapHolderInfoReceived} =
        props;
    const {device, holder} = iotProduct || {};
    const {isHolderConnected, name = '', type} = device || {};
    const {status: holderStatus} = holder || {};
    const [isDevicePairedBefore, setIsDevicePairedBefore] = useState(null);
    const [isDeviceDisconnectedBefore, setIsDeviceDisconnectedBefore] = useState(false);
    const isDeviceReady = iotDeviceConnectionStatusService.isDeviceReady(connectionStatus);
    const isDeviceDisconnectedWithNoReconnect = iotDeviceConnectionStatusService.isDeviceDisconnected(connectionStatus);
    const isHolderRegistered = productService.getIsRegistered(holderStatus);
    const isReady =
        isDeviceReady &&
        isYapDeviceInfoReceived &&
        (!isHolderConnected ||
            (isHolderConnected && isYapHolderInfoReceived) ||
            (isHolderConnected && !isHolderRegistered));

    useEffect(() => {
        if (isDeviceDisconnectedWithNoReconnect) {
            setIsDevicePairedBefore(false);
        }
    }, [isDeviceDisconnectedWithNoReconnect]);

    const onConnectionStatusChange = () => {
        const localizedStrings = getLocalizedStrings();
        const isDevicePaired = iotDeviceConnectionStatusService.isDevicePaired(connectionStatus);
        const isDeviceConnected = iotDeviceConnectionStatusService.isDeviceConnected(connectionStatus);

        const showSuccessReconnectNotification = () => {
            const connectionNotificationText = localizedStrings.formatString(
                localizedStrings[localizationKeys.DEVICE_RECONNECT_NOTIFICATION],
                name
            );

            dispatch(setNotificationSuccess(connectionNotificationText));
            analyticsService.pushDeviceReconnectNotificationEvent(type);
        };

        if (isDevicePaired || isDeviceConnected) {
            setIsDevicePairedBefore(true);
        }
        if (isDeviceDisconnectedWithNoReconnect) {
            setIsDevicePairedBefore(false);
            return;
        }
        if (isDevicePairedBefore) {
            if (isReady) {
                showSuccessReconnectNotification();
                modalService.hideModal(ModalTypes.RECONNECT_DEVICE_GUIDE);
            } else {
                appErrorService.showDeviceDisconnectNotification();
            }
        }
    };

    useDidUpdate(() => {
        setIsDeviceDisconnectedBefore(!isDeviceReady);

        if (!isReconnectGuideEnabled) return;

        if ((isDeviceDisconnectedBefore && !isDeviceReady) || (!isDeviceDisconnectedBefore && isDeviceReady)) return;

        onConnectionStatusChange();
    }, [isReady]);

    return null;
};

export default connect(mapStateToProps)(DeviceConnectionDetector);
